import { Box, Grid, Heading } from '@chakra-ui/react';
import { Kong } from '@cyberkongz/database';
import {
  ArrowDownIcon,
  Card,
  fetchHttp,
  FormButtonsWaiting4Confirmation,
  FormCompleted,
  FormWithTxStage,
  getKongImageByType,
  Image,
  KongActionIcon,
  Modal,
  pxToRem,
  useSharedApiRoutes,
  WithExpectedChainConnection,
} from '@cyberkongz/ui';
import {
  ChainEnhanced,
  getKongzContract,
  useChainEnhanced,
  useWriteContractWithTracking,
} from '@cyberkongz/ui-blockchain';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { decodeEventLog, TransactionReceipt } from 'viem';
import { useSimulateContract } from 'wagmi';

function parseEvolveLogs({ logs }: TransactionReceipt, chain?: ChainEnhanced) {
  let genes: string | undefined;
  const abi = getKongzContract(chain).abi;

  logs.forEach((log) => {
    try {
      const { data, topics } = log;
      const parsedLog = decodeEventLog({
        abi,
        data,
        topics,
      });

      if (parsedLog.eventName === 'KongAscended') {
        genes = parsedLog.args.genes.toString();
      }
    } catch (err) {
      console.error('Something went wrong while parsing data', log, err);
    }
  });

  return genes;
}

function useEvolve({ id }: { id: number }) {
  const [stage, setStage] = useState<FormWithTxStage>('waiting4Confirmation');
  const { chain } = useChainEnhanced();
  const { SHARED_API_ROUTES } = useSharedApiRoutes();
  const { data: dataSimulate } = useSimulateContract({
    ...getKongzContract(chain),
    functionName: 'evolve',
    args: [BigInt(id)],
    query: {
      enabled: chain?.type === 'mainnet',
    },
  });
  const [genes, setGenes] = useState<string>();
  const { data } = useQuery({
    queryKey: ['/api/kong/baby-image/', genes],
    queryFn: () =>
      fetchHttp<{ image: string }>(SHARED_API_ROUTES.GET_KONG_BABY_IMAGE(genes as string)),
    retry: false,
    enabled: !!genes,
  });

  const { txHash, writeContract } = useWriteContractWithTracking({
    dataSimulate,
    onInProgress: () => setStage('inProgress'),
    onCompleted: (receipt) => {
      if (receipt) {
        setGenes(parseEvolveLogs(receipt, chain));
      }
      setStage('completed');
    },
  });

  const isLastStage = stage === 'completed';

  console.log('data', data);

  return {
    stage,
    setStage,
    isLastStage,
    txHash,
    writeContract,
    imageEvolved: data?.image,
  };
}

export function EvolveIncubator({ kong }: { kong: Kong }) {
  const { id } = kong;
  const [isOpened, setIsOpened] = useState(false);
  const { stage, setStage, txHash, writeContract, isLastStage, imageEvolved } = useEvolve({ id });

  return (
    <>
      <KongActionIcon label="Evolve" onClick={() => setIsOpened(true)}>
        <ArrowDownIcon transform="rotateZ(180deg)" css={{ width: `${pxToRem(14)}!important` }} />
      </KongActionIcon>
      <Modal
        variant="form"
        isOpen={isOpened}
        onClose={() => setIsOpened(false)}
        headerComponent={!isLastStage ? <Heading size="xs">Evolve Baby</Heading> : null}
      >
        <WithExpectedChainConnection expectedChain="mainnet">
          {!isLastStage ? (
            <Grid gap={6}>
              <Box>Please confirm Evolve process of Baby below.</Box>
              <Card>
                <Grid gap={1} textStyle="p-sm" fontWeight="medium">
                  <Image
                    borderRadius="sm"
                    boxSize={24}
                    src={getKongImageByType(id, 'baby')}
                    alt={`Kong ${id} preview`}
                  />
                  {`Baby ${id}`}
                </Grid>
              </Card>
              <FormButtonsWaiting4Confirmation
                stage={stage}
                setStage={setStage}
                label="Close"
                onClickPrev={() => setIsOpened(false)}
                onClickNext={writeContract}
              />
            </Grid>
          ) : (
            <FormCompleted
              onClose={() => setIsOpened(false)}
              txHash={txHash}
              title="Baby evolved"
              summary={imageEvolved ? <Image src={imageEvolved} alt="Evolved Baby image" /> : null}
            />
          )}
        </WithExpectedChainConnection>
      </Modal>
    </>
  );
}
